"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getCurrentPlaybackPosition = getCurrentPlaybackPosition;
exports.handleVisibilityRestore = handleVisibilityRestore;
exports.resumePlayback = resumePlayback;
exports.setMediaSessionActions = setMediaSessionActions;
exports.syncPlayerToDesiredTime = syncPlayerToDesiredTime;
var _moment = _interopRequireDefault(require("moment"));
/**
 * Calculates the elapsed time since the media started playing.
 *
 * @param {boolean} isPlaying - Indicates whether the media is currently playing.
 * @param {number} startedOn - The Unix timestamp when the media started playing.
 * @param {number} seek - The current seek position in the media.
 * @param {number} serverTime - The current server time as a Unix timestamp.
 * @param {number} [serverTimeDelta] - The difference between the client and server times in seconds.
 * @returns {number} The elapsed time since the media started playing, adjusted for seek and server time differences.
 */
function getTimeElapsed(isPlaying, startedOn, seek, serverTime, serverTimeDelta) {
  // If media is paused, elapsed will be equal to seek. In this scenario
  // startedOn is undefined and irrelevant
  if (!isPlaying) return seek;
  var diff;
  if (serverTimeDelta !== undefined) {
    // NEW - we use serverTimeDelta to calculate serverTime
    var calculatedServerTime = (0, _moment["default"])().utc().unix() - serverTimeDelta;
    diff = calculatedServerTime - startedOn;
  } else {
    // LEGACY - we have serverTime
    diff = serverTime - startedOn;
  }
  return (diff < 0 ? 0 : diff) + seek;
}

/**
 * Returns the current playback position of the media element.
 *
 * @param {boolean} isPlaying - Indicates whether the media is currently playing.
 * @param {number} startedOn - The Unix timestamp when the media started playing.
 * @param {number} seek - The current seek position in the media.
 * @param {number} duration - The duration of the media in seconds.
 * @param {number} serverTime - The current server time as a Unix timestamp.
 * @param {number} [serverTimeDelta] - The difference between the client and server times in seconds.
 * @param {boolean} loop - Indicates whether the media should loop.
 */
function getCurrentPlaybackPosition(isPlaying, startedOn, seek, duration, serverTime, serverTimeDelta, loop) {
  // calculate a proper `currentTime` attribute to set for the playerRef
  if (duration) {
    //The time since the video started playing
    var timeElapsed = getTimeElapsed(isPlaying, startedOn, seek, serverTime, serverTimeDelta);
    var desiredCurrentTime;
    if (timeElapsed <= duration) {
      desiredCurrentTime = timeElapsed;
    } else if (timeElapsed > duration && !loop) {
      console.log("getCurrentPlaybackPosition(): timeElapsed is greater than duration, setting currentTime to duration");
      desiredCurrentTime = duration;
    } else {
      console.log("getCurrentPlaybackPosition(): timeElapsed is greater than duration, using modulo to loop the desiredCurrentTime");
      // take into account any loops
      desiredCurrentTime = timeElapsed % duration;
    }
    if (isNaN(desiredCurrentTime)) return;
    console.log("getCurrentPlaybackPosition(): currentTime set to ".concat(desiredCurrentTime));
    return desiredCurrentTime;
  }
}

/**
 * Resumes playback of the video if it is paused and meets the specified conditions.
 *
 * @param {Object} player - The Video.js player instance.
 * @param {number} duration - The total duration of the video in seconds.
 * @param {boolean} loop - Indicates whether the video should loop when it reaches the end.
 * @returns {void}
 */
function resumePlayback(player, duration, loop) {
  // video is "over" and has run out already, and should not play
  if (!loop && player.currentTime() === duration) {
    return;
  }
  var playPromise = player.play();
  if (playPromise === undefined) {
    return;
  }
  playPromise["catch"](function (error) {
    console.log('Failed to resume video playback ', error);
  });
}

/**
 * Synchronizes the Video.js player's currentTime to the desired position,
 * handling finished playback, active playback, and pause states.
 *
 * @param {Object} player - The Video.js player instance.
 * @param {Object} options
 * @param {number|undefined} options.desiredCurrentTime - Target playback position.
 * @param {number} options.duration - Media duration in seconds.
 * @param {boolean} options.loop - Whether the media should loop.
 * @param {boolean} options.canPlay - Whether the player has fired canplay.
 * @param {boolean} options.isPlaying - Whether the media should be playing.
 * @param {number} options.startedOn - Unix timestamp when media started.
 */
function syncPlayerToDesiredTime(player, _ref) {
  var desiredCurrentTime = _ref.desiredCurrentTime,
    duration = _ref.duration,
    loop = _ref.loop,
    canPlay = _ref.canPlay,
    isPlaying = _ref.isPlaying,
    startedOn = _ref.startedOn;
  if (desiredCurrentTime !== undefined) {
    player.currentTime(desiredCurrentTime);
  }
  if (canPlay && isPlaying && player.paused()) {
    // Handle race condition when stream starts and content is shown simultaneously:
    // Socket delivery delays can cause attendees to receive startedOn timestamp after
    // video metadata loads. If duration is still 0, getCurrentPlaybackPosition returns
    // undefined (it requires duration to calculate position), preventing currentTime from
    // being set. This leaves the player in an undefined state where resumePlayback won't
    // work properly. Explicitly set currentTime(0) to ensure playback begins.
    if (startedOn && desiredCurrentTime === undefined && duration === 0) {
      player.currentTime(0);
    }
    resumePlayback(player, duration, loop);
  }
  if (!isPlaying && !player.paused()) {
    player.pause();
  }
}

/**
 * Restores video playback position when the tab becomes visible again.
 * Muted videos may be paused by the browser when the tab is inactive.
 *
 * @param {Object} player - The Video.js player instance.
 * @param {Object} options
 * @param {boolean} options.isPlaying - Whether the media should be playing.
 * @param {number} options.startedOn - Unix timestamp when media started.
 * @param {number} options.seek - Current seek position.
 * @param {number} options.duration - Media duration in seconds.
 * @param {number} options.serverTime - Server time as Unix timestamp.
 * @param {number} options.serverTimeDelta - Client/server time difference.
 * @param {boolean} options.loop - Whether the media should loop.
 * @param {boolean} options.canPlay - Whether the player has fired canplay.
 */
function handleVisibilityRestore(player, _ref2) {
  var isPlaying = _ref2.isPlaying,
    startedOn = _ref2.startedOn,
    seek = _ref2.seek,
    duration = _ref2.duration,
    serverTime = _ref2.serverTime,
    serverTimeDelta = _ref2.serverTimeDelta,
    loop = _ref2.loop,
    canPlay = _ref2.canPlay;
  //if the tab was hidden, but is now visible, set the seek position to where it "should" be.
  //Browsers may throttle or pause media playback in background tabs, so when the tab becomes active
  //again, it is necessary to reset the seek position to where it should be based on the start time
  if (document.visibilityState !== 'visible') return;
  var desiredCurrentTime = getCurrentPlaybackPosition(isPlaying, startedOn, seek, duration, serverTime, serverTimeDelta, loop);
  if (desiredCurrentTime === undefined || desiredCurrentTime >= duration) return;
  player.currentTime(desiredCurrentTime);
  if (canPlay && isPlaying && player.paused()) {
    resumePlayback(player, duration, loop);
  }
}

/*
 * Updates the Media Session API action handlers
 */
function setMediaSessionActions(handler) {
  if (!('mediaSession' in navigator)) {
    return;
  }
  var actions = ['play', 'pause', 'seekbackward', 'seekforward', 'seekto', 'stop'];
  for (var _i = 0, _actions = actions; _i < _actions.length; _i++) {
    var action = _actions[_i];
    try {
      navigator.mediaSession.setActionHandler(action, handler);
    } catch (e) {
      console.error("Failed to update ".concat(action, " action of media session : "), e);
    }
  }
}